import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import api from "@/lib/api";
import { useFilters } from "@/context/FiltersContext";
import useHome from "@/lib/useHome";
import { money, haversineKm, fmtDate, addedMoneyForEvents, eventMoneyBreakdown, dollarsPerMile, daysUntilMountain, mountainTodayISO, moneyLabel } from "@/lib/ui";
import { useCredits } from "@/context/CreditsContext";
import { getChoices, pickPerf, toggleEnteredPerf, rodeoEntered } from "@/lib/choices";
import usePersistentState from "@/lib/usePersistentState";
import { monthsInRange } from "@/components/MonthNav";
import { DateRangeButtons } from "@/components/DateRangeButtons";
import { TimeGrid } from "@/components/TimeGridCalendar";
import { PickCircle } from "@/components/PickCircle";
import { EntryChips } from "@/components/EntryChips";
import RodeoActions from "@/components/RodeoActions";
import RodeoInfoDialog from "@/components/RodeoInfoDialog";
import ContestantCard from "@/components/ContestantCard";
import useFollowing from "@/lib/useFollowing";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import useIsMobile from "@/lib/useIsMobile";
import MobileCalendar from "@/pages/mobile/MobileCalendar";
import MobileMap from "@/pages/mobile/MobileMap";
import MobileTable from "@/pages/mobile/MobileTable";
import { MonthCalendar, DayPeekDialog } from "@/pages/CalendarView";
import { Loader2, CalendarDays, MapPin, LocateFixed, ChevronLeft, ChevronRight, Clock, LayoutList, SlidersHorizontal, Check, Search, Coins, AlertTriangle, DollarSign, Plus, Star, Crown, Users } from "lucide-react";

const TILES = {
  street: { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "&copy; OpenStreetMap" },
  satellite: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Tiles &copy; Esri" },
};
const COLORS = { me: "#0EA5E9", sel: "#1D4ED8", grey: "#9AA1AB", home: "#E68A2E" };
const dot = (r) => (r.associations || []).includes("CPRA") ? "#C8000F" : (r.associations || []).includes("PRCA") ? "#1D4ED8" : "#9333EA";
const pinIcon = (color, n) => L.divIcon({ className: "", iconSize: [22, 22], iconAnchor: [11, 11], html: `<div class="rp-map-pin" style="background:${color};${n ? "" : "width:14px;height:14px;"}">${n || ""}</div>` });
const hiIcon = () => L.divIcon({ className: "", iconSize: [36, 36], iconAnchor: [18, 18], html: `<div class="rp-map-pin" style="background:#D6009A;width:22px;height:22px;border:2px solid #fff;box-shadow:0 0 0 6px #D6009A55"></div>` });
const homeIcon = () => L.divIcon({ className: "", iconSize: [28, 28], iconAnchor: [14, 14], html: `<div class="rp-map-pin" style="background:${COLORS.home};border:2px solid #fff;font-size:14px">⌂</div>` });
const meIcon = () => L.divIcon({ className: "", iconSize: [22, 22], iconAnchor: [11, 11], html: `<div style="width:16px;height:16px;border-radius:50%;background:${COLORS.me};border:3px solid #fff;box-shadow:0 0 0 3px ${COLORS.me}55"></div>` });
function FlyTo({ loc }) { const map = useMap(); useEffect(() => { if (loc) map.flyTo([loc.lat, loc.lng], 8, { duration: 1 }); }, [loc, map]); return null; }

// Compact "Your week" KPI strip for the Table/Calendar top band. Fills the dead
// space beside the Same-day travel check with at-a-glance, tappable stats plus a
// credits/top-up tile that nudges wallet top-ups right where users plan entries.
function WeekStatStrip({ stats, closingSoon, onToggleClosing, credits, onWallet }) {
  const Tile = ({ icon: Icon, label, value, sub, onClick, active, accent, testid }) => (
    <button
      onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors min-w-[132px] ${
        onClick ? "cursor-pointer" : "cursor-default"
      } ${active
        ? "bg-[#C8000F] border-[#C8000F] text-white"
        : accent === "gold"
          ? "bg-[#FBF4E6] border-[#E6B35A]/50 text-[#8A5A00] hover:bg-[#F3E6C6]"
          : accent === "warn" && value > 0
            ? "bg-[#FEF2F2] border-[#FBD5D5] text-[#B42318] hover:bg-[#FCE8E8]"
            : "bg-white border-[#E4E7EB] text-[#14181F] hover:border-[#C8000F]/40"}`}>
      <span className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
        active ? "bg-white/20" : accent === "gold" ? "bg-[#E6B35A]/20" : accent === "warn" && value > 0 ? "bg-[#FBD5D5]" : "bg-[#F4F6F8]"}`}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-lg font-extrabold leading-none tabular-nums">{value}</span>
        <span className={`block text-[11px] font-semibold leading-tight mt-0.5 ${active ? "text-white/80" : "opacity-70"}`}>{label}</span>
        {sub && <span className={`block text-[10px] leading-tight ${active ? "text-white/70" : "opacity-55"}`}>{sub}</span>}
      </span>
    </button>
  );
  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid="nv-week-stats">
      <Tile icon={Clock} label="Closing in 7 days" value={stats.closing} sub={closingSoon ? "filter on — tap to clear" : "tap to filter"}
        onClick={onToggleClosing} active={closingSoon} testid="nv-stat-closing" />
      <Tile icon={AlertTriangle} label={stats.clashes === 1 ? "clash to resolve" : "clashes to resolve"} value={stats.clashes} accent="warn" testid="nv-stat-clashes" />
      <Tile icon={DollarSign} label="added money in view" value={money(stats.added)} testid="nv-stat-added" />
      <Tile icon={LayoutList} label="rodeos matched" value={stats.matched} testid="nv-stat-matched" />
      <Tile icon={credits > 0 ? Coins : Plus} label={credits > 0 ? "Rodeo Credits" : "Credits — top up"} value={credits == null ? "—" : credits.toLocaleString()}
        sub="tap for wallet" onClick={onWallet} accent="gold" testid="nv-stat-credits" />
    </div>
  );
}

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const mEnd = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const wStart = (d) => { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; };
const wEnd = (d) => { const x = wStart(d); x.setDate(x.getDate() + 6); return x; };
const parseHour = (t) => { if (!t) return null; const m = String(t).match(/(\d{1,2})(?::(\d{2}))?\s*([AaPp][Mm])?/); if (!m) return null; let h = +m[1]; const min = m[2] ? +m[2] : 0; const ap = m[3] ? m[3].toLowerCase() : null; if (ap === "pm" && h !== 12) h += 12; if (ap === "am" && h === 12) h = 0; return h + min / 60; };

function CalNav({ calView, setCalView, dateRange, setCustomRange }) {
  const anchor = dateRange.from ? new Date(dateRange.from + "T00:00:00") : new Date();
  const snap = (view, d) => {
    if (view === "month") setCustomRange(iso(mStart(d)), iso(mEnd(d)));
    else if (view === "week") setCustomRange(iso(wStart(d)), iso(wEnd(d)));
    else setCustomRange(iso(d), iso(d));
  };
  const changeView = (v) => { setCalView(v); snap(v, anchor); };
  const step = (dir) => { const d = new Date(anchor); if (calView === "month") d.setMonth(d.getMonth() + dir); else if (calView === "week") d.setDate(d.getDate() + dir * 7); else d.setDate(d.getDate() + dir); snap(calView, d); };
  const goToday = () => snap(calView, new Date());
  let label;
  if (calView === "month") label = anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  else if (calView === "week") { const s = wStart(anchor), e = wEnd(anchor); const sameM = s.getMonth() === e.getMonth(); label = `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${e.toLocaleDateString("en-US", sameM ? { day: "numeric", year: "numeric" } : { month: "short", day: "numeric", year: "numeric" })}`; }
  else label = anchor.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  const VIEWS = [{ v: "day", l: "Day" }, { v: "week", l: "Week" }, { v: "month", l: "Month" }];
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="cal-nav">
      <div className="flex rounded-lg overflow-hidden border border-[#D7DBE0]">
        {VIEWS.map((o) => (
          <button key={o.v} onClick={() => changeView(o.v)} data-testid={`nv-calview-${o.v}`}
            className={`text-[11px] font-semibold px-2.5 py-1.5 ${calView === o.v ? "bg-[#0C1017] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{o.l}</button>
        ))}
      </div>
      <div className="flex items-center border border-[#E4E7EB] rounded-lg overflow-hidden">
        <button onClick={() => step(-1)} data-testid="nv-cal-prev" className="px-2 py-1.5 hover:bg-[#F1F3F5]"><ChevronLeft className="w-4 h-4" /></button>
        <span className="px-2.5 py-1.5 text-[12px] font-semibold min-w-[120px] text-center" data-testid="nv-cal-label">{label}</span>
        <button onClick={() => step(1)} data-testid="nv-cal-next" className="px-2 py-1.5 hover:bg-[#F1F3F5]"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <button onClick={goToday} data-testid="nv-cal-today" className="flex items-center gap-1.5 border border-[#E4E7EB] rounded-lg px-2.5 py-1.5 text-[12px] font-medium hover:bg-[#F1F3F5]"><CalendarDays className="w-4 h-4" /> Today</button>
      <DateRangeButtons />
    </div>
  );
}

// Popover to set 1st / 2nd choice performances for a rodeo (used by Table rows).
function PerfPicker({ rodeo, choices, onPick, children }) {
  const ch = choices[rodeo.id] || {};
  const perfs = rodeo.performances || [];
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end" onClick={(e) => e.stopPropagation()}>
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 px-1 mb-1.5">Pick 1st &amp; 2nd choice</div>
        {perfs.length ? (
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {perfs.map((p) => {
              const num = ch.pref1 === p.id ? 1 : ch.pref2 === p.id ? 2 : null;
              return (
                <div key={p.id} role="button" tabIndex={0} onClick={() => onPick(rodeo, p)} data-testid={`table-pick-${rodeo.id}-${p.id}`}
                  className={`w-full flex items-center justify-between gap-2 text-left px-2 py-1.5 rounded-md border cursor-pointer ${num ? "bg-[#FDECEE] border-[#C8000F]/40" : "bg-[#F7F8FA] border-transparent hover:border-slate-300"}`}>
                  <span className="min-w-0">
                    <span className="text-sm font-medium flex items-center gap-1.5">
                      <span className={`text-[9px] font-bold px-1 rounded ${p.type === "slack" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>{p.type === "slack" ? "SLACK" : "PERF"}</span>
                      {p.label}
                    </span>
                    <span className="block text-[11px] text-slate-500">{fmtDate(p.date)}{p.time ? ` · ${p.time}` : ""}</span>
                  </span>
                  <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-extrabold border ${num ? "bg-[#C8000F] text-white border-[#C8000F]" : "bg-white text-slate-400 border-slate-300"}`}>{num || "+"}</span>
                </div>
              );
            })}
          </div>
        ) : <p className="text-sm text-slate-500 px-1 py-2">Performance schedule not posted yet.</p>}
      </PopoverContent>
    </Popover>
  );
}

// The single source-of-truth rodeo card — used by the left list (Calendar/Map)
// AND as the frozen first column of the Table timeline, so every view matches.
function RodeoListCard({ r, ch, onClick }) {
  const { displayEvents } = useFilters();
  const amtV = addedMoneyForEvents(r, displayEvents).amount ?? (r.added_money || 0);
  const label = moneyLabel(r, displayEvents);
  const dpm = isFinite(r.km) && r.km > 0 && amtV ? Math.round(amtV / (r.km / 1.60934)) : null;
  return (
    <div className="px-4 py-3">
      <button onClick={onClick} className="text-left block w-full">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot(r) }} />
          <span className="font-display font-bold text-[15px]">{r.name}</span>
          {r.associations.map((a) => <span key={a} className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-[#D7DBE0] text-slate-600">{a}</span>)}
          {(ch.pref1 || ch.pref2 || Object.values(ch.entered || {}).some(Boolean)) && (
            <span className="ml-auto flex items-center gap-1">
              {ch.pref1 && <span className="w-4 h-4 rounded-full bg-[#C8000F] text-white text-[9px] font-extrabold flex items-center justify-center">1</span>}
              {ch.pref2 && <span className="w-4 h-4 rounded-full bg-[#0C1017] text-white text-[9px] font-extrabold flex items-center justify-center">2</span>}
              {Object.values(ch.entered || {}).some(Boolean) && <span className="w-4 h-4 rounded-full bg-[#16A34A] text-white flex items-center justify-center"><Check className="w-2.5 h-2.5" strokeWidth={3} /></span>}
            </span>
          )}
        </div>
        <div className="text-[12px] text-slate-500">{r.city}, {r.state}</div>
        <div className="text-[12px] text-slate-600 mt-0.5">{label ? <span className="text-[#3B6B1E] font-semibold">{label}</span> : null}{isFinite(r.km) ? <>{label ? <span className="text-slate-300"> • </span> : null}{r.km.toLocaleString()} km away</> : null}{dpm ? <> <span className="text-slate-300">•</span> <span className="text-[#3B6B1E] font-semibold">${dpm.toLocaleString()}/mi</span></> : null}</div>
        <EntryChips r={r} className="mt-1.5" />
      </button>
      <div className="mt-2"><RodeoActions rodeo={r} /></div>
    </div>
  );
}


export default function CombinedView({ mode = "table" }) {
  const isMobile = useIsMobile();
  if (isMobile) return mode === "map" ? <MobileMap /> : mode === "table" ? <MobileTable /> : <MobileCalendar />;
  return <CombinedDesktop mode={mode} />;
}

function CombinedDesktop({ mode }) {
  const nav = useNavigate();
  const { matches, assoc, toggleAssoc, setAssocList, circuits, toggleCircuit, clearCircuits, minMoney, setMinMoney, dateRange, setCustomRange, rodeos, selectRodeo, selectedRodeoId, closingSoon, toggleClosingSoon, clearSelection, events, displayEvents } = useFilters();
  const home = useHome();
  const right = mode; // "table" | "calendar" | "map" — driven by the route
  const { credits } = useCredits();
  const [calView, setCalView] = usePersistentState("rp_nv_calview", "month");
  const [nvSort, setNvSort] = usePersistentState("rp_nv_sort", "date");
  const [filtersOpen, setFiltersOpen] = usePersistentState("rp_nv_filters_open", false);
  const [choices, setChoices] = useState(getChoices());
  const pick = (r, p) => setChoices(pickPerf(r.id, p.id));
  const enter = (r, p) => setChoices(toggleEnteredPerf(r.id, p.id));
  useEffect(() => {
    const sync = () => setChoices(getChoices());
    window.addEventListener("rp-choices-changed", sync);
    return () => window.removeEventListener("rp-choices-changed", sync);
  }, []);
  const [highlight, setHighlight] = useState(null); // { id, from: "card" | "entry" }
  const [mapFocusId, setMapFocusId] = useState(null); // persistent map pin spotlight (until next pick)
  const [mapSearch, setMapSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const tableMatches = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return [];
    return (rodeos || []).filter((r) => `${r.name} ${r.city} ${r.state}`.toLowerCase().includes(q)).slice(0, 12);
  }, [tableSearch, rodeos]);
  // A searched rodeo that the current filters would otherwise hide (force-shown in the view).
  const forcedSel = useMemo(() => {
    if (!selectedRodeoId) return null;
    const r = (rodeos || []).find((x) => x.id === selectedRodeoId);
    if (!r || matches(r)) return null;
    return r;
  }, [selectedRodeoId, rodeos, matches]);
  const [info, setInfo] = useState(null);
  const [peek, setPeek] = useState(null);
  const [mapType, setMapType] = usePersistentState("rp_nv_map_type", "street");
  const [enteredMapOnly, setEnteredMapOnly] = usePersistentState("rp_nv_map_entered", false);
  const [cMode, setCMode] = usePersistentState("rp_nv_riders_mode", false);
  const { user } = useAuth();
  const isPro = !!user?.is_pro;
  const { following, isFollowing, follow, unfollow } = useFollowing();
  const [nvSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState({});
  const [activeRider, setActiveRider] = useState(null);
  const [rQ, setRQ] = useState("");
  const [rResults, setRResults] = useState(null);
  const [cEvent, setCEvent] = usePersistentState("rp_nv_cevent", "Team Roping");
  const [cData, setCData] = useState(null);
  const [cQ, setCQ] = useState("");
  const [card, setCard] = useState(null);
  const [myLoc, setMyLoc] = useState(null);
  const [geo, setGeo] = usePersistentState("rp_geo_cache_v2", {});
  const [roads, setRoads] = useState({});
  const triedGeo = useRef(new Set());
  const leftRef = useRef(null);
  const calRef = useRef(null);

  const allAssoc = ["CPRA", "PRCA", "CCA"];
  const allCircuits = useMemo(() => [...new Set((rodeos || []).map((r) => r.circuit_name).filter(Boolean))].sort(), [rodeos]);
  const months = monthsInRange(dateRange);

  const highlightId = highlight?.id || null;
  const hiId = highlight?.id || selectedRodeoId || null; // persistent spotlight across views
  const flash = (id, from) => { setHighlight({ id, from, t: Date.now() }); };
  const resolvedCoords = (r) => {
    if (r.lat && r.lng) return { lat: r.lat, lng: r.lng };
    const k = `${(r.city || "").trim().toLowerCase()}|${(r.state || "").trim().toLowerCase()}`;
    const c = coordIndex[k] || geo[k];
    return c && c.lat ? c : null;
  };
  // Highlight + fly to a rodeo on the map; if it has no coords yet (common for US/PRCA towns),
  // geocode just that one on-demand so the pin appears and the map flies to it.
  const focusRodeo = (r) => {
    setMapFocusId(r.id);
    if (resolvedCoords(r)) return;
    const city = (r.city || "").trim(), state = (r.state || "").trim();
    if (!city) return;
    const k = `${city.toLowerCase()}|${state.toLowerCase()}`;
    triedGeo.current.add(k);
    api.post("/geocode/batch", { places: [{ city, state, country: r.country || (state.length === 2 ? "United States" : "Canada") }] })
      .then((res) => { const g = (res.data.results || [])[0]; if (g && g.lat) setGeo((prev) => ({ ...prev, [k]: { lat: g.lat, lng: g.lng } })); })
      .catch(() => {});
  };
  const onCardClick = (r) => {
    if (selectedRodeoId === r.id) { clearSelection(); setMapFocusId(null); setHighlight(null); return; } // tap again to deselect
    flash(r.id, "card"); focusRodeo(r); selectRodeo(r, { moveRange: false });
  };   // highlight its entry in the active pane
  const onEntryClick = (r) => { flash(r.id, "entry"); selectRodeo(r, { moveRange: false }); }; // highlight its card in the list (no reorder)
  useEffect(() => {
    if (!highlight) return;
    const timer = setTimeout(() => setHighlight(null), 2600);
    setTimeout(() => {
      const sel = highlight.from === "card" ? `[data-entry="${highlight.id}"]` : `[data-card="${highlight.id}"]`;
      const el = document.querySelector(sel);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    return () => clearTimeout(timer);
  }, [highlight]);

  // Parity with Calendar/Map: when a rodeo is selected (from search or another view) OR when the
  // user switches INTO the table/calendar view with a selection already active, scroll its row into
  // view so the highlight is immediately visible. Order of the list is never changed.
  useEffect(() => {
    const targetId = selectedRodeoId || hiId;
    if (!targetId) return;
    let cancelled = false;
    let tries = 0;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-card="${targetId}"]`);
      if (!el || el.offsetParent === null) {
        if (tries++ < 20) setTimeout(tryScroll, 80); // grid still rendering — retry ~1.6s
        return;
      }
      let sc = el.parentElement;
      while (sc && sc !== document.body) {
        const oy = getComputedStyle(sc).overflowY;
        if ((oy === "auto" || oy === "scroll") && sc.scrollHeight > sc.clientHeight) break;
        sc = sc.parentElement;
      }
      if (!sc || sc === document.body) { el.scrollIntoView({ behavior: "smooth", block: "center" }); return; }
      const er = el.getBoundingClientRect(), sr = sc.getBoundingClientRect();
      if (er.top < sr.top || er.bottom > sr.bottom) el.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    const t = setTimeout(tryScroll, 80);
    return () => { cancelled = true; clearTimeout(t); };
  }, [selectedRodeoId, hiId, right, rodeos]);

  // Two-way scroll sync (Calendar month view only): scrolling the left rodeo list moves the
  // calendar to the matching month, and scrolling the calendar moves the list to that month.
  useEffect(() => {
    if (right !== "calendar" || calView !== "month") return;
    const leftEl = leftRef.current, calEl = calRef.current;
    if (!leftEl || !calEl) return;
    let leftLock = false, calLock = false, raf = 0;
    const topMonth = (container, sel, key) => {
      const cTop = container.getBoundingClientRect().top;
      let cur = null;
      for (const el of container.querySelectorAll(sel)) {
        if (el.getBoundingClientRect().top - cTop <= 12) cur = el; else break;
      }
      return cur ? key(cur) : null;
    };
    const scrollTo = (container, el) => {
      const t = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 6;
      container.scrollTo({ top: Math.max(0, t) });
    };
    const onLeft = () => {
      if (leftLock) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const mo = topMonth(leftEl, "[data-month]", (el) => el.dataset.month);
        const target = mo && calEl.querySelector(`[data-testid="month-${mo}"]`);
        if (!target) return;
        calLock = true; scrollTo(calEl, target);
        setTimeout(() => { calLock = false; }, 140);
      });
    };
    const onCal = () => {
      if (calLock) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const mo = topMonth(calEl, '[data-testid^="month-"]', (el) => el.getAttribute("data-testid").replace("month-", ""));
        const target = mo && leftEl.querySelector(`[data-month="${mo}"]`);
        if (!target) return;
        leftLock = true; scrollTo(leftEl, target);
        setTimeout(() => { leftLock = false; }, 140);
      });
    };
    leftEl.addEventListener("scroll", onLeft, { passive: true });
    calEl.addEventListener("scroll", onCal, { passive: true });
    return () => { leftEl.removeEventListener("scroll", onLeft); calEl.removeEventListener("scroll", onCal); cancelAnimationFrame(raf); };
  }, [right, calView, rodeos, dateRange.from, dateRange.to]);

  const firstDate = (r) => {
    const perfs = (r.performances || []).filter((p) => p.date);
    const inWin = perfs.filter((p) => !dateRange.from || (p.date >= dateRange.from && p.date <= dateRange.to));
    const src = (inWin.length ? inWin : perfs).slice().sort((a, b) => a.date.localeCompare(b.date) || (parseHour(a.time) ?? -1) - (parseHour(b.time) ?? -1));
    if (!src.length) return "9999-12-31T9999";
    const p = src[0]; const h = parseHour(p.time);
    return `${p.date}T${String(h == null ? 0 : Math.round(h * 60)).padStart(4, "0")}`;
  };
  // Rest-of-season scope: hide rodeos whose last performance is before today, so every
  // count (incl. the "rodeos matched" KPI + $ filter) answers "what's left this season".
  // Mirrors the mobile views' hasFuture/rangeIsPast gate so all three stay in sync; a
  // deliberately past date range still shows its rodeos.
  const _nvToday = mountainTodayISO();
  const _rangeIsPast = !!(dateRange?.to && dateRange.to < _nvToday);
  const inSeason = (r) => _rangeIsPast || (r.performances || []).some((p) => p.date && p.date >= _nvToday);
  const list = useMemo(() => {
    const base0 = (right === "map" && enteredMapOnly)
      ? (rodeos || []).filter((r) => inSeason(r) && rodeoEntered(choices, r.id))
      : (rodeos || []).filter((r) => inSeason(r) && matches(r));
    // Always include the explicitly selected rodeo (e.g. picked from search) so it stays
    // visible even when the current filters would exclude it. Only THAT out-of-filter
    // rodeo is pinned to the top — a normal in-list card tap must NOT reorder/jump.
    let base = base0;
    let pinnedId = null;
    if (selectedRodeoId && !base0.some((r) => r.id === selectedRodeoId)) {
      const sel = (rodeos || []).find((r) => r.id === selectedRodeoId);
      if (sel) { base = [...base0, sel]; pinnedId = selectedRodeoId; }
    }
    return base
    .map((r) => ({ ...r, km: home && r.lat && r.lng ? Math.round(haversineKm(home, { lat: r.lat, lng: r.lng })) : Infinity }))
    .sort((a, b) => {
      // Pin only an out-of-filter searched rodeo to the very top; a normal tap stays in place.
      if (pinnedId) {
        if (a.id === pinnedId && b.id !== pinnedId) return -1;
        if (b.id === pinnedId && a.id !== pinnedId) return 1;
      }
      if (nvSort === "money") return (b.added_money || 0) - (a.added_money || 0);
      if (nvSort === "near") return a.km - b.km;
      if (nvSort === "value") return ((dollarsPerMile(b, home, events) ?? -1) - (dollarsPerMile(a, home, events) ?? -1));
      const c = firstDate(a).localeCompare(firstDate(b)); // by date, then biggest purse
      return c !== 0 ? c : (b.added_money || 0) - (a.added_money || 0);
    });
  }, [rodeos, matches, nvSort, home, dateRange.from, dateRange.to, right, enteredMapOnly, choices, selectedRodeoId, events]);

  const allEvents = useMemo(() => [...new Set((rodeos || []).flatMap((r) => r.events || []))].sort(), [rodeos]);
  useEffect(() => {
    const rp = nvSearchParams.get("rider");
    if (rp) { setCMode(true); setActiveRider(rp); if (!isFollowing(rp)) follow(rp); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!cMode) return;
    const names = activeRider ? [activeRider] : following;
    names.forEach((n) => { if (!profiles[n]) api.get(`/competitors/profile?name=${encodeURIComponent(n)}`).then((r) => setProfiles((p) => ({ ...p, [n]: r.data }))).catch(() => {}); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cMode, following, activeRider]);
  useEffect(() => {
    const qq = rQ.trim();
    if (qq.length < 2) { setRResults(null); return; }
    const t = setTimeout(() => api.get(`/competitors/search?q=${encodeURIComponent(qq)}`).then((r) => setRResults(r.data || [])).catch(() => setRResults([])), 300);
    return () => clearTimeout(t);
  }, [rQ]);
  const riderColorHex = (status) => status === "won" ? "#3B8A2B" : status === "upcoming" ? COLORS.sel : COLORS.grey;
  const riderPins = useMemo(() => {
    const names = activeRider ? [activeRider] : following;
    const out = [];
    names.forEach((n) => { const prof = profiles[n]; if (!prof) return; (prof.pins || []).forEach((pin) => { if (pin.lat && pin.lng) out.push({ rider: n, ...pin }); }); });
    return out;
  }, [profiles, following, activeRider]);
  const cById = useMemo(() => Object.fromEntries((cData?.rodeos || []).map((r) => [r.rodeo_id, r])), [cData]);

  const dayMap = useMemo(() => {
    const mp = {};
    for (const r of (rodeos || []).filter((r) => inSeason(r) && matches(r))) {
      const seen = new Set();
      for (const p of (r.performances || [])) { if (p.date && !seen.has(p.date)) { seen.add(p.date); (mp[p.date] = mp[p.date] || []).push(r); } }
    }
    return mp;
  }, [rodeos, matches]);
  const perfsByDay = useMemo(() => {
    const mp = {};
    for (const r of (rodeos || []).filter((r) => inSeason(r) && matches(r))) { for (const p of (r.performances || [])) { if (!p.date) continue; (mp[p.date] = mp[p.date] || []).push({ rodeo: r, perf: p }); } }
    return mp;
  }, [rodeos, matches]);

  const anchorDate = dateRange.from ? new Date(dateRange.from + "T00:00:00") : new Date();
  const weekDays = useMemo(() => { const s = wStart(anchorDate); return Array.from({ length: 7 }, (_, i) => { const d = new Date(s); d.setDate(s.getDate() + i); return iso(d); }); }, [dateRange.from]);
  const dayISO = iso(anchorDate);
  const gridDates = useMemo(() => {
    const from = dateRange.from ? new Date(dateRange.from + "T00:00:00") : mStart(new Date());
    const to = dateRange.to ? new Date(dateRange.to + "T00:00:00") : mEnd(new Date());
    const arr = []; const d = new Date(from); let guard = 0;
    while (d <= to && guard < 120) { arr.push(iso(d)); d.setDate(d.getDate() + 1); guard++; }
    return arr;
  }, [dateRange.from, dateRange.to]);
  useEffect(() => { if (calView !== "month" && dateRange.from && dateRange.to) { const span = (new Date(dateRange.to) - new Date(dateRange.from)) / 864e5; if (span > 10) setCalView("month"); } }, [dateRange.from, dateRange.to]); // eslint-disable-line

  // Clash + travel check — only two DIFFERENT rodeos each with a #1 (pref1) on the same day.
  const rodeoById = useMemo(() => Object.fromEntries((rodeos || []).map((r) => [r.id, r])), [rodeos]);
  const coordIndex = useMemo(() => { const idx = {}; (rodeos || []).forEach((r) => { if (r.lat && r.lng) { const k = `${(r.city || "").trim().toLowerCase()}|${(r.state || "").trim().toLowerCase()}`; if (!idx[k]) idx[k] = { lat: r.lat, lng: r.lng }; } }); return idx; }, [rodeos]);
  const pref1Picks = useMemo(() => {
    const arr = [];
    (rodeos || []).forEach((r) => { const ch = choices[r.id]; if (ch?.pref1) { const p = (r.performances || []).find((x) => x.id === ch.pref1); if (p?.date) arr.push({ rodeo: r, perf: p }); } });
    return arr;
  }, [rodeos, choices]);

  const dayChecks = useMemo(() => {
    const AVG_KMH = 90, BUFFER_H = 1.0;
    const byDate = {};
    pref1Picks.forEach((it) => { (byDate[it.perf.date] = byDate[it.perf.date] || []).push(it); });
    const rank = { ok: 0, tight: 1, unknown: 2, clash: 3 };
    const out = [];
    for (const [date, items] of Object.entries(byDate)) {
      if (new Set(items.map((i) => i.rodeo.id)).size < 2) continue;
      const picks = items.map((it) => {
        const r = it.rodeo; let lat = r.lat, lng = r.lng;
        if (!(lat && lng)) { const c = coordIndex[`${(r.city || "").trim().toLowerCase()}|${(r.state || "").trim().toLowerCase()}`] || geo[`${(r.city || "").trim().toLowerCase()}|${(r.state || "").trim().toLowerCase()}`]; if (c && c.lat) { lat = c.lat; lng = c.lng; } }
        return { name: r.name, city: r.city, state: r.state, lat, lng, time: it.perf.time, startH: parseHour(it.perf.time) };
      }).sort((a, b) => (a.startH ?? 99) - (b.startH ?? 99));
      const legs = []; let worst = "ok";
      for (let i = 1; i < picks.length; i++) {
        const a = picks[i - 1], b = picks[i];
        const bothCoords = !!(a.lat && a.lng && b.lat && b.lng);
        const routeKey = bothCoords ? `${a.lat},${a.lng}|${b.lat},${b.lng}` : null;
        const road = routeKey ? roads[routeKey] : undefined;
        const routed = !!road; const pending = bothCoords && road === undefined;
        const km = road ? road.km : (bothCoords ? Math.round(haversineKm({ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng })) : null);
        const driveH = road ? road.h : (km != null ? km / AVG_KMH : null);
        const gapH = (a.startH != null && b.startH != null) ? (b.startH - a.startH) : null;
        let verdict, basis;
        if (driveH == null) { verdict = "unknown"; basis = "no-loc"; }
        else if (gapH == null) { basis = "dist"; verdict = driveH > 9 ? "clash" : driveH > 4 ? "tight" : "ok"; }
        else { basis = "time"; verdict = gapH < driveH ? "clash" : gapH < driveH + BUFFER_H ? "tight" : "ok"; }
        legs.push({ a, b, km, driveH, gapH, verdict, basis, routed, pending, routeKey });
        if (rank[verdict] > rank[worst]) worst = verdict;
      }
      if (legs.length) out.push({ date, picks, legs, worst });
    }
    return out.sort((a, b) => a.date.localeCompare(b.date));
  }, [pref1Picks, coordIndex, geo, roads]);

  // "Your week" KPI strip stats — derived from the already-filtered `list`, the
  // day-clash checks, and the active event filter (so added money mirrors the cards).
  const weekStats = useMemo(() => {
    const today = mountainTodayISO();
    const closing = (list || []).filter((r) => {
      if (!r.entry_close || r.entry_close < today) return false;
      const d = daysUntilMountain(r.entry_close);
      return d != null && d >= 0 && d <= 7;
    }).length;
    const clashes = dayChecks.filter((dc) => dc.worst === "clash").length;
    const added = (list || []).reduce((s, r) => s + (addedMoneyForEvents(r, displayEvents).amount || 0), 0);
    return { closing, clashes, added, matched: (list || []).length };
  }, [list, dayChecks, displayEvents]);


  useEffect(() => {
    const todo = [];
    dayChecks.forEach((dc) => dc.legs.forEach((l) => { if (l.routeKey && !(l.routeKey in roads) && !todo.find((t) => t.key === l.routeKey)) todo.push({ key: l.routeKey, a: l.a, b: l.b }); }));
    if (!todo.length) return;
    let abort = false;
    (async () => { for (const p of todo) { try { const coords = `${p.a.lng},${p.a.lat};${p.b.lng},${p.b.lat}`; const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=false`); const d = await res.json(); const rt = d?.routes?.[0]; if (!abort) setRoads((prev) => ({ ...prev, [p.key]: rt ? { km: Math.round(rt.distance / 1000), h: rt.duration / 3600 } : null })); } catch { if (!abort) setRoads((prev) => ({ ...prev, [p.key]: null })); } } })();
    return () => { abort = true; };
  }, [dayChecks, roads]);

  useEffect(() => {
    const seen = new Set(); const need = [];
    pref1Picks.forEach((it) => {
      const r = it.rodeo; const city = (r.city || "").trim(), state = (r.state || "").trim();
      const k = `${city.toLowerCase()}|${state.toLowerCase()}`;
      const resolved = (r.lat && r.lng) || coordIndex[k] || (geo[k] && geo[k].lat);
      if (!resolved && city && !seen.has(k) && !triedGeo.current.has(k)) { seen.add(k); need.push({ city, state, country: r.country || (state.length === 2 ? "United States" : "Canada"), _k: k }); }
    });
    if (!need.length) return;
    let abort = false;
    need.forEach((n) => triedGeo.current.add(n._k));
    api.post("/geocode/batch", { places: need.map(({ city, state, country }) => ({ city, state, country })) })
      .then((res) => { if (abort) return; const upd = {}; (res.data.results || []).forEach((r) => { if (r.lat && r.lng) upd[String(r.key).toLowerCase()] = { lat: r.lat, lng: r.lng }; }); if (Object.keys(upd).length) setGeo((prev) => ({ ...prev, ...upd })); })
      .catch(() => {});
    return () => { abort = true; };
  }, [pref1Picks, coordIndex, geo]); // eslint-disable-line

  const pinned = useMemo(() => {
    const arr = [...(list || [])];
    // Always include the explicitly selected rodeo (e.g. picked from search) even if the
    // current date / association / money filters would otherwise exclude it.
    if (selectedRodeoId && !arr.some((r) => r.id === selectedRodeoId)) {
      const sel = (rodeos || []).find((r) => r.id === selectedRodeoId);
      if (sel) arr.push(sel);
    }
    return arr.map((r) => {
      if (r.lat && r.lng) return r;
      const k = `${(r.city || "").trim().toLowerCase()}|${(r.state || "").trim().toLowerCase()}`;
      const c = coordIndex[k] || geo[k];
      return c && c.lat ? { ...r, lat: c.lat, lng: c.lng } : null;
    }).filter(Boolean);
  }, [list, rodeos, selectedRodeoId, coordIndex, geo]);

  // In MAP view, geocode any visible rodeo that has no stored coordinates so the pins (and the
  // click-to-highlight / fly-to) still work even when the deployed DB hasn't been geo-backfilled.
  useEffect(() => {
    if (right !== "map") return;
    const seen = new Set(); const need = [];
    (list || []).forEach((r) => {
      if (r.lat && r.lng) return;
      const city = (r.city || "").trim(), state = (r.state || "").trim();
      const k = `${city.toLowerCase()}|${state.toLowerCase()}`;
      if (!city || coordIndex[k] || geo[k] || seen.has(k) || triedGeo.current.has(k)) return;
      seen.add(k); need.push({ city, state, country: r.country || (state.length === 2 ? "United States" : "Canada"), _k: k });
    });
    if (!need.length) return;
    let abort = false;
    need.forEach((n) => triedGeo.current.add(n._k));
    api.post("/geocode/batch", { places: need.map(({ city, state, country }) => ({ city, state, country })) })
      .then((res) => { if (abort) return; const upd = {}; (res.data.results || []).forEach((r) => { if (r.lat && r.lng) upd[String(r.key).toLowerCase()] = { lat: r.lat, lng: r.lng }; }); if (Object.keys(upd).length) setGeo((prev) => ({ ...prev, ...upd })); })
      .catch(() => {});
    return () => { abort = true; };
  }, [right, list, coordIndex, geo]); // eslint-disable-line

  const mapMatches = useMemo(() => {
    const q = mapSearch.trim().toLowerCase();
    if (!q) return [];
    // Search the FULL rodeo set so any rodeo is findable on the map regardless of the
    // active date / association / money filters.
    return (rodeos || []).filter((r) => `${r.name} ${r.city} ${r.state}`.toLowerCase().includes(q)).slice(0, 12);
  }, [mapSearch, rodeos]);
  const focusLoc = useMemo(() => {
    if (!mapFocusId) return null;
    const r = pinned.find((x) => x.id === mapFocusId);
    return r ? { lat: r.lat, lng: r.lng, _t: mapFocusId } : null;
  }, [mapFocusId, pinned]);
  const cRodeos = useMemo(() => {
    const qq = cQ.trim().toLowerCase();
    return pinned.map((r) => { const cd = cById[r.id]; const contestants = cd?.contestants || []; const matched = qq ? contestants.filter((n) => n.toLowerCase().includes(qq)) : []; return { rodeo_id: r.id, name: r.name, lat: r.lat, lng: r.lng, city: r.city, state: r.state, associations: r.associations, count: contestants.length, contestants, matched, hasData: !!cd }; }).filter((r) => !qq || r.matched.length);
  }, [pinned, cById, cQ]);
  const cWithData = useMemo(() => cRodeos.filter((r) => r.hasData).length, [cRodeos]);
  const locate = () => { if (!navigator.geolocation) return; navigator.geolocation.getCurrentPosition((pos) => setMyLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude }), () => {}, { enableHighAccuracy: true, timeout: 8000 }); };

  const title = right === "map" ? "Map View" : right === "calendar" ? "Calendar View" : "Table View";

  const filterPanel = (
    <div className="p-3 border-b border-[#E4E7EB] bg-[#FAFBFC] sticky top-0 z-10 space-y-2">
      <div className="flex items-center justify-between">
        <button onClick={() => setFiltersOpen((v) => !v)} data-testid="nv-filters-toggle" className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 hover:text-[#C8000F]">
          <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${filtersOpen ? "rotate-90" : ""}`} />
        </button>
        <span className="text-[11px] text-slate-400">{list.length} rodeos</span>
      </div>
      <button onClick={toggleClosingSoon} data-testid="nv-closing-soon"
        className={`flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1.5 rounded-full border transition-colors ${closingSoon ? "bg-[#C8000F] border-[#C8000F] text-white" : "bg-white border-[#E4E7EB] text-slate-600 hover:border-[#C8000F] hover:text-[#C8000F]"}`}>
        <Clock className="w-3.5 h-3.5" /> Closes in 7 days
        {closingSoon && <Check className="w-3.5 h-3.5" />}
      </button>
      {filtersOpen && (
        <div className="space-y-2 pt-1" data-testid="nv-filters">
          <div className="flex flex-wrap gap-1.5" data-testid="nv-assoc-filter">
            {allAssoc.map((a) => (
              <button key={a} onClick={() => toggleAssoc(a)} data-testid={`nv-assoc-${a}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${assoc.has(a) ? "bg-[#C8000F] text-white border-[#C8000F]" : "bg-white text-slate-600 border-[#D7DBE0] hover:border-slate-400"}`}>{a}</button>
            ))}
            {assoc.size > 0 && <button onClick={() => setAssocList([])} className="text-[11px] text-slate-500 hover:underline px-1">Clear</button>}
          </div>
          {allCircuits.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="nv-circuit-filter">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-0.5 self-center">Circuit</span>
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="nv-circuit-trigger" className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${circuits.size > 0 ? "bg-[#1D4ED8] text-white border-[#1D4ED8]" : "bg-white text-slate-600 border-[#D7DBE0] hover:border-slate-400"}`}>
                    {circuits.size > 0 ? `${circuits.size} circuit${circuits.size > 1 ? "s" : ""}` : "All circuits"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="start">
                  <div className="flex items-center justify-between px-1 mb-1.5">
                    <button onClick={() => (circuits.size === allCircuits.length ? clearCircuits() : allCircuits.forEach((c) => { if (!circuits.has(c)) toggleCircuit(c); }))} data-testid="nv-circuit-select-all" className="text-[11px] font-bold uppercase tracking-wide text-[#1D4ED8] hover:underline">
                      {circuits.size === allCircuits.length ? "Deselect all" : "Select all"}
                    </button>
                    {circuits.size > 0 && <button onClick={clearCircuits} data-testid="nv-circuit-clear" className="text-[11px] text-[#C8000F] font-semibold hover:underline">Clear</button>}
                  </div>
                  <div className="space-y-0.5 max-h-72 overflow-y-auto">
                    {allCircuits.map((c) => {
                      const on = circuits.has(c);
                      return (
                        <button key={c} onClick={() => toggleCircuit(c)} data-testid={`nv-circuit-${c.replace(/[\s()]/g, "-")}`}
                          className={`w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-md text-sm ${on ? "bg-[#1D4ED8]/10 text-[#1D4ED8]" : "hover:bg-[#F1F3F5] text-slate-700"}`}>
                          <span className={`w-4 h-4 rounded flex items-center justify-center border shrink-0 ${on ? "bg-[#1D4ED8] border-[#1D4ED8]" : "border-slate-300"}`}>{on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}</span>
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5" data-testid="nv-money-filter">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-0.5 self-center">Added $</span>
            {[0, 7500, 15000, 30000].map((mn) => (
              <button key={mn} onClick={() => setMinMoney(mn)} data-testid={`nv-money-${mn}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${minMoney === mn ? "bg-[#E68A2E] text-white border-[#E68A2E]" : "bg-white text-slate-600 border-[#D7DBE0] hover:border-slate-400"}`}>{mn === 0 ? "Any" : `$${mn / 1000}k+`}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="nv-sort">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-0.5 self-center">Sort</span>
            {[{ v: "date", l: "By date" }, { v: "near", l: "Nearest" }, { v: "money", l: "Biggest purse" }, { v: "value", l: "Best $/mi" }].map((o) => (
              <button key={o.v} onClick={() => setNvSort(o.v)} data-testid={`nv-sort-${o.v}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${nvSort === o.v ? "bg-[#3B6B1E] text-white border-[#3B6B1E]" : "bg-white text-slate-600 border-[#D7DBE0] hover:border-slate-400"}`}>{o.l}</button>
            ))}
          </div>
          {right === "map" ? <div className="pt-1"><DateRangeButtons /></div> : <div className="pt-1"><CalNav calView={calView} setCalView={setCalView} dateRange={dateRange} setCustomRange={setCustomRange} /></div>}
        </div>
      )}
    </div>
  );

  const listCards = list.map((r) => {
    const ch = choices[r.id] || {};
    const hot = hiId === r.id;
    return (
      <div key={r.id} data-card={r.id} data-month={firstDate(r).slice(0, 7)} data-testid={`nv-list-${r.id}`} className={`border-b border-[#EEF0F2] ${hot ? "bg-[#FDECEE] ring-2 ring-inset ring-[#C8000F]" : "hover:bg-[#FAFBFC]"}`}>
        <RodeoListCard r={r} ch={ch} onClick={() => onCardClick(r)} />
      </div>
    );
  });

  return (
    <div>
      <div className="border-b border-[#E4E7EB] bg-white px-4 sm:px-6 pt-5 pb-4 flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold tracking-tight">{title}</h1>
        {forcedSel && (
          <button onClick={clearSelection} data-testid="nv-clear-search" className="flex items-center gap-2 text-xs font-semibold bg-[#FDE7E9] text-[#C8000F] px-3 py-1.5 rounded-full hover:bg-[#F9D2D6]">
            Showing “{forcedSel.name}” <span className="text-slate-400">·</span> Back to my list ×
          </button>
        )}
      </div>

      {rodeos === null ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
      ) : right === "table" ? (
        // TABLE: mirrors the Calendar frame — filters (400px) top-left, Same-day travel check
        // top-right, then the timeline whose frozen 400px left column IS the card list (aligned to dates).
        <div className="flex flex-col h-[calc(100vh-140px)]">
          <div className="relative z-50 flex items-stretch border-b border-[#E4E7EB] bg-[#FAFBFC]">
            <div className="w-[400px] shrink-0 border-r border-[#E4E7EB]">{filterPanel}</div>
            <div className="flex-1 min-w-0 overflow-y-auto max-h-[240px] p-3">
              <WeekStatStrip stats={weekStats} closingSoon={closingSoon} onToggleClosing={toggleClosingSoon} credits={credits} onWallet={() => nav("/wallet")} />
              <ClashPanel dayChecks={dayChecks} />
            </div>
          </div>
          <div className="relative z-40 border-b border-[#E4E7EB] bg-white px-3 py-2" data-testid="nv-table-search">
            <div className="relative max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} placeholder="Search a rodeo (any date / association)…" data-testid="nv-table-search-input"
                className="w-full text-sm bg-[#F4F6F8] border border-[#D7DBE0] rounded-lg pl-9 pr-8 py-2 focus:outline-none focus:border-[#C8000F]" />
              {tableSearch && <button onClick={() => setTableSearch("")} data-testid="nv-table-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-lg leading-none">×</button>}
              {tableSearch.trim() && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E4E7EB] rounded-lg shadow-lg max-h-72 overflow-auto z-50">
                  {tableMatches.length ? tableMatches.map((r) => (
                    <button key={r.id} data-testid={`nv-table-search-result-${r.id}`} onClick={() => { selectRodeo(r); flash(r.id, "card"); setTableSearch(""); }}
                      className="flex items-center justify-between w-full text-left px-3 py-2 hover:bg-[#FAFBFC] border-b border-[#F1F3F5] last:border-0">
                      <span className="min-w-0"><span className="font-semibold text-sm text-[#14181F] block truncate">{r.name}</span>
                        <span className="text-[12px] text-slate-500">{[r.city, r.state].filter(Boolean).join(", ")}{(r.associations || []).length ? ` · ${(r.associations || []).join(", ")}` : ""}</span></span>
                      <span className="text-[11px] text-slate-400 shrink-0 ml-2">{fmtDate((r.performances || []).map((p) => p.date).filter(Boolean).sort()[0] || r.start_date || "")}</span>
                    </button>
                  )) : <div className="px-3 py-3 text-sm text-slate-400">No rodeos match “{tableSearch}”.</div>}
                </div>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-auto" data-testid="nv-table">
            <TableTimeline list={list} dates={gridDates} choices={choices} onPick={pick} onEntered={enter} onCard={onCardClick} highlightId={hiId} />
          </div>
        </div>
      ) : right === "calendar" ? (
        // CALENDAR: top bar matches the Table (380px filters + travel-check beside it);
        // below it the rodeo card list stays on the left with the calendar on the right.
        <div className="flex flex-col h-[calc(100vh-140px)]">
          <div className="flex items-stretch border-b border-[#E4E7EB] shrink-0">
            <div className="w-[400px] shrink-0 border-r border-[#E4E7EB]">{filterPanel}</div>
            <div className="flex-1 min-w-0 overflow-y-auto max-h-[240px] p-3">
              <WeekStatStrip stats={weekStats} closingSoon={closingSoon} onToggleClosing={toggleClosingSoon} credits={credits} onWallet={() => nav("/wallet")} />
              {dayChecks.length > 0 && <ClashPanel dayChecks={dayChecks} />}
            </div>
          </div>
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-[400px_1fr] min-h-0">
            <div ref={leftRef} className="overflow-y-auto border-r border-[#E4E7EB]" data-testid="nv-cal-list">
              <div className="relative sticky top-0 z-20 bg-white border-b border-[#E4E7EB] p-2">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} placeholder="Search a rodeo (any date / association)…" data-testid="nv-cal-search-input"
                  className="w-full text-sm bg-[#F4F6F8] border border-[#D7DBE0] rounded-lg pl-9 pr-8 py-2 focus:outline-none focus:border-[#C8000F]" />
                {tableSearch && <button onClick={() => setTableSearch("")} data-testid="nv-cal-search-clear" className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-lg leading-none">×</button>}
                {tableSearch.trim() && (
                  <div className="absolute left-2 right-2 top-full mt-1 bg-white border border-[#E4E7EB] rounded-lg shadow-lg max-h-72 overflow-auto z-50">
                    {tableMatches.length ? tableMatches.map((r) => (
                      <button key={r.id} data-testid={`nv-cal-search-result-${r.id}`} onClick={() => { selectRodeo(r); flash(r.id, "card"); setTableSearch(""); }}
                        className="flex items-center justify-between w-full text-left px-3 py-2 hover:bg-[#FAFBFC] border-b border-[#F1F3F5] last:border-0">
                        <span className="min-w-0"><span className="font-semibold text-sm text-[#14181F] block truncate">{r.name}</span>
                          <span className="text-[12px] text-slate-500">{[r.city, r.state].filter(Boolean).join(", ")}{(r.associations || []).length ? ` · ${(r.associations || []).join(", ")}` : ""}</span></span>
                        <span className="text-[11px] text-slate-400 shrink-0 ml-2">{fmtDate((r.performances || []).map((p) => p.date).filter(Boolean).sort()[0] || r.start_date || "")}</span>
                      </button>
                    )) : <div className="px-3 py-3 text-sm text-slate-400">No rodeos match “{tableSearch}”.</div>}
                  </div>
                )}
              </div>
              {listCards}
            </div>
            <div ref={calRef} className="overflow-y-auto px-5 py-4 space-y-4" data-testid="nv-calendar">
              {calView === "month" ? (
                <div className="space-y-6">
                  {months.map((mo) => (
                    <MonthCalendar key={`${mo.y}-${mo.m}`} y={mo.y} m={mo.m} dayMap={dayMap}
                      onPeek={(d, l) => setPeek({ date: d, list: l })}
                      onEntry={(r) => onEntryClick(r)} onPick={pick} onEntered={enter} choices={choices} highlightId={hiId} />
                  ))}
                </div>
              ) : calView === "week" ? (
                <TimeGrid days={weekDays} perfsByDay={perfsByDay} choices={choices} onPick={pick} onEntered={enter} onEntry={onEntryClick} highlightId={hiId} />
              ) : (
                <TimeGrid days={[dayISO]} perfsByDay={perfsByDay} choices={choices} onPick={pick} onEntered={enter} onEntry={onEntryClick} highlightId={hiId} />
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] h-[calc(100vh-140px)]">
          {/* LEFT: collapsible filters + rodeo list */}
          <div ref={leftRef} className="overflow-y-auto border-r border-[#E4E7EB]">
            {filterPanel}
            {listCards}
          </div>

          {/* RIGHT: Calendar / Map */}
          <div className="relative min-h-[400px]">
            {right === "map" ? (
              <>
                <div className="absolute top-3 left-14 z-[1000] flex flex-col gap-2" data-testid="nv-map-view-mode">
                  <div className="flex rounded-lg overflow-hidden shadow-md border border-black/10">
                    <button onClick={() => setCMode(false)} data-testid="nv-map-mode-rodeos" className={`text-xs font-semibold px-3 py-1.5 ${!cMode ? "bg-[#C8000F] text-white" : "bg-white text-slate-600"}`}>Rodeos</button>
                    <button onClick={() => setCMode(true)} data-testid="nv-map-mode-contestants" className={`text-xs font-semibold px-3 py-1.5 flex items-center gap-1 ${cMode ? "bg-[#C8000F] text-white" : "bg-white text-slate-600"}`}>Riders</button>
                  </div>
                  {cMode && (
                    <div className="bg-white rounded-lg shadow-md border border-black/10 p-2 w-64 space-y-2" data-testid="nv-map-rider-tools">
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input value={rQ} onChange={(e) => setRQ(e.target.value)} placeholder="Search a rider to follow…" data-testid="nv-map-rider-search" className="w-full text-xs border border-[#D7DBE0] rounded pl-7 pr-2 py-1.5" />
                      </div>
                      {rQ.trim().length >= 2 && (
                        <div className="rounded border border-[#E4E7EB] max-h-40 overflow-y-auto" data-testid="nv-map-rider-results">
                          {rResults && rResults.length ? rResults.map((r) => {
                            const f = isFollowing(r.name);
                            return (
                              <button key={r.name} onClick={() => (f ? unfollow(r.name) : follow(r.name))} data-testid={`nv-map-rider-follow-${r.name}`}
                                className="flex items-center justify-between gap-2 w-full text-left px-2 py-1.5 hover:bg-slate-50 border-b border-[#F0F1F3] last:border-0">
                                <span className="min-w-0"><span className="block text-xs font-semibold text-slate-800 truncate">{r.name}</span>{r.hometown && <span className="block text-[10px] text-slate-500 truncate">{r.hometown}</span>}</span>
                                <Star className={`w-3.5 h-3.5 shrink-0 ${f ? "fill-current text-[#C8000F]" : "text-slate-400"}`} />
                              </button>
                            );
                          }) : <div className="px-2 py-3 text-[11px] text-slate-400">No riders match “{rQ}”.</div>}
                        </div>
                      )}
                      {following.length > 0 && (
                        <div className="flex flex-wrap gap-1" data-testid="nv-map-rider-chips">
                          <button onClick={() => setActiveRider(null)} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${!activeRider ? "bg-[#C8000F] text-white" : "bg-[#F1F3F5] text-slate-600"}`}>All ({following.length})</button>
                          {following.map((n) => (
                            <button key={n} onClick={() => setActiveRider(activeRider === n ? null : n)} data-testid={`nv-map-rider-chip-${n}`}
                              className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${activeRider === n ? "bg-[#C8000F] text-white" : "bg-[#F1F3F5] text-slate-600"}`}>{n}</button>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[10px] text-slate-500 pt-0.5">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#3B8A2B]" /> Won</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: COLORS.grey }} /> No $</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: COLORS.sel }} /> Upcoming</span>
                      </div>
                      {!following.length && <div className="text-[10px] text-slate-500">Follow a rider to see their rodeos plotted here.</div>}
                    </div>
                  )}
                </div>
                {!cMode && (
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] w-72" data-testid="nv-map-search">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input value={mapSearch} onChange={(e) => setMapSearch(e.target.value)} placeholder="Search a rodeo, then jump to it…" data-testid="nv-map-search-input"
                        className="w-full text-sm rounded-lg shadow-md border border-black/10 pl-8 pr-3 py-2 bg-white focus:outline-none focus:border-[#C8000F]" />
                    </div>
                    {mapSearch.trim() && (
                      <div className="mt-1 bg-white rounded-lg shadow-lg border border-black/10 max-h-72 overflow-y-auto" data-testid="nv-map-search-results">
                        {mapMatches.length ? mapMatches.map((r) => (
                          <button key={r.id} data-testid={`nv-map-search-result-${r.id}`} onClick={() => { focusRodeo(r); selectRodeo(r); setMapSearch(""); }}
                            className="flex items-center gap-2 w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-[#F0F1F3] last:border-0">
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: dot(r) }} />
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-slate-800 truncate">{r.name}</span>
                              <span className="block text-[11px] text-slate-500 truncate">{[r.city, r.state].filter(Boolean).join(", ")}</span>
                            </span>
                          </button>
                        )) : <div className="px-3 py-3 text-sm text-slate-400">No rodeos match “{mapSearch}”.</div>}
                      </div>
                    )}
                  </div>
                )}
                <div className="absolute top-3 right-3 z-[1000] flex items-center gap-2" data-testid="nv-map-type-toggle">
                  <button onClick={() => setEnteredMapOnly((v) => !v)} data-testid="nv-map-entered-toggle" title="Show only rodeos you've entered (green)"
                    className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-md border ${enteredMapOnly ? "bg-[#16A34A] border-[#16A34A] text-white" : "bg-white border-black/10 text-slate-700 hover:bg-slate-50"}`}>
                    <Check className="w-3.5 h-3.5" /> Entered
                  </button>
                  <button onClick={locate} data-testid="nv-map-locate-btn" title="Center on my location" className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg shadow-md border border-black/10 bg-white text-slate-700 hover:bg-slate-50"><LocateFixed className="w-3.5 h-3.5 text-[#0EA5E9]" /> Track me</button>
                  <div className="flex rounded-lg overflow-hidden shadow-md border border-black/10">
                    <button onClick={() => setMapType("street")} data-testid="nv-map-type-street" className={`text-xs font-semibold px-3 py-1.5 ${mapType === "street" ? "bg-[#1D4ED8] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Map</button>
                    <button onClick={() => setMapType("satellite")} data-testid="nv-map-type-satellite" className={`text-xs font-semibold px-3 py-1.5 ${mapType === "satellite" ? "bg-[#1D4ED8] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Satellite</button>
                  </div>
                </div>
                <MapContainer center={[home?.lat || 52, home?.lng || -108]} zoom={5} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                  <TileLayer url={TILES[mapType].url} attribution={TILES[mapType].attribution} />
                  {mapType === "satellite" && <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />}
                  <FlyTo loc={myLoc} />
                  <FlyTo loc={focusLoc} />
                  {myLoc && <Marker position={[myLoc.lat, myLoc.lng]} icon={meIcon()}><Popup><div className="font-semibold">You are here</div></Popup></Marker>}
                  {home && <Marker position={[home.lat, home.lng]} icon={homeIcon()}><Popup>Your home base</Popup></Marker>}
                  {cMode ? riderPins.map((p, idx) => (
                    <Marker key={`${p.rider}-${p.rodeo_id}-${idx}`} position={[p.lat, p.lng]} icon={pinIcon(riderColorHex(p.status))}>
                      <Popup>
                        <div className="font-semibold">{p.rider}</div>
                        <div className="text-slate-500">{p.name}{p.city ? ` · ${p.city}` : ""}</div>
                        {p.status === "upcoming" ? (
                          <div className="mt-1 text-slate-600">
                            <div className="text-[#1D4ED8] font-semibold">Entered — up next</div>
                            {p.upcoming.map((u, i) => <div key={i}>{u.event}{u.role ? ` (${u.role})` : ""} · {u.performance_label || ""}{u.performance_date ? ` · ${u.performance_date}` : ""}{u.position ? ` · #${u.position}` : ""}</div>)}
                          </div>
                        ) : (
                          <div className="mt-1 text-slate-600">
                            <div className={`font-semibold ${p.status === "won" ? "text-[#16A34A]" : "text-slate-500"}`}>{p.status === "won" ? `Won ${money(p.earnings)}` : "Competed · no cheque"}</div>
                            <div className="mt-0.5 max-h-40 overflow-y-auto">{p.results.map((r, i) => <div key={i}>{r.event} · {r.place || "—"}{r.value ? ` · ${r.value}` : ""}{r.earnings > 0 ? ` · ${money(r.earnings)}` : ""}</div>)}</div>
                          </div>
                        )}
                      </Popup>
                    </Marker>
                  )) : pinned.map((r) => (
                    <Marker key={r.id} position={[r.lat, r.lng]} icon={mapFocusId === r.id ? hiIcon() : pinIcon(enteredMapOnly ? "#3B8A2B" : dot(r))} eventHandlers={{ click: () => { onEntryClick(r); setMapFocusId(r.id); } }}>
                      <Popup>
                        <div className="font-semibold">{r.name}</div>
                        <div className="text-slate-500">{r.city}, {r.state}</div>
                        <div className="text-slate-600 mt-0.5">{moneyLabel(r, displayEvents)}</div>
                        <button onClick={() => setInfo(r)} className="mt-1 text-[#C8000F] font-semibold block">View info →</button>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </>
            ) : null}
          </div>
        </div>
      )}

      <DayPeekDialog peek={peek} onClose={() => setPeek(null)} onOpen={(r) => { setPeek(null); onEntryClick(r); }} />
      <RodeoInfoDialog data={info} onClose={() => setInfo(null)} />
      <ContestantCard open={!!card} rodeoId={card?.rodeoId} name={card?.name} onClose={() => setCard(null)} />
    </div>
  );
}

const GRID_INFO_W = 400, GRID_DAY_W = 84;
// Strip redundant weekday + time-of-day words from a performance label — the column already
// shows the date and the time is shown below, so "1st Performance Thursday Night" -> "1st Performance".
const cleanPerfLabel = (s) => {
  const out = (s || "")
    .replace(/\b(mon|tue|tues|wed|weds|thu|thur|thurs|fri|sat|sun|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/gi, "")
    .replace(/\b(morning|matinee|afternoon|evening|night|day)\b/gi, "")
    .replace(/\s{2,}/g, " ").replace(/\s*[-–—/]\s*$/, "").replace(/^\s*[-–—/]\s*/, "").trim();
  return out || (s || "");
};
function TableTimeline({ list, dates, choices, onPick, onEntered, onCard, highlightId }) {
  const rows = list.filter((r) => (r.performances || []).some((p) => p.date && dates.includes(p.date)));
  const todayISO = new Date().toISOString().slice(0, 10);
  if (!rows.length) return <div className="text-center py-16 text-slate-400 px-5">No rodeos with performances in this date range.</div>;
  return (
    <div className="min-w-max">
      <div className="flex sticky top-0 z-30 bg-[#F7F8FA] border-b border-[#E4E7EB]">
        <div className="sticky left-0 z-40 bg-[#F7F8FA] border-r border-[#E4E7EB] px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-slate-400 flex items-end" style={{ width: GRID_INFO_W, minWidth: GRID_INFO_W }}>Rodeo</div>
        {dates.map((d) => {
          const dt = new Date(d + "T00:00:00"); const today = d === todayISO;
          return (
            <div key={d} className="border-r border-[#E4E7EB] text-center py-1.5" style={{ width: GRID_DAY_W, minWidth: GRID_DAY_W }}>
              <div className="text-[9px] font-bold uppercase text-slate-400">{dt.toLocaleDateString("en-US", { weekday: "short" })}</div>
              <div className={`text-sm font-bold ${today ? "text-white bg-[#C8000F] rounded-full w-6 h-6 flex items-center justify-center mx-auto" : "text-slate-700"}`}>{dt.getDate()}</div>
              <div className="text-[9px] text-slate-400">{dt.toLocaleDateString("en-US", { month: "short" })}</div>
            </div>
          );
        })}
      </div>
      {rows.map((r) => {
        const ch = choices[r.id] || {}; const hot = highlightId === r.id;
        return (
          <div key={r.id} data-entry={r.id} data-card={r.id} data-testid={`nv-table-row-${r.id}`} className={`flex border-b border-[#EEF0F2] ${hot ? "ring-2 ring-inset ring-[#C8000F]" : ""}`}>
            <div className={`sticky left-0 z-10 border-r border-[#E4E7EB] ${hot ? "bg-[#FDECEE]" : "bg-white"}`} style={{ width: GRID_INFO_W, minWidth: GRID_INFO_W }}>
              <RodeoListCard r={r} ch={ch} onClick={() => onCard(r)} />
            </div>
            {dates.map((d) => {
              const perfs = (r.performances || []).filter((p) => p.date === d);
              return (
                <div key={d} className={`border-r border-[#EEF0F2] p-1 ${hot ? "bg-[#FDECEE]" : "bg-white"}`} style={{ width: GRID_DAY_W, minWidth: GRID_DAY_W }}>
                  {perfs.map((p) => {
                    const num = ch.pref1 === p.id ? 1 : ch.pref2 === p.id ? 2 : null;
                    const ent = !!(ch.entered || {})[p.id];
                    return (
                      <div key={p.id} className={`relative rounded border px-1 py-0.5 mb-1 text-left min-h-[48px] ${ent ? "bg-[#DCF5D6] border-[#16A34A]" : num === 1 ? "bg-[#EAF3EC] border-[#166534]" : "bg-white border-[#E4E7EB]"}`}>
                        <PickCircle split size={18} num={num} entered={ent} onClick={() => onPick(r, p)} onToggleEntered={() => onEntered(r, p)} testid={`grid-pick-${d}-${r.id}-${p.id}`} />
                        <div className="font-semibold text-slate-700 leading-tight pr-6 text-[10px]">{cleanPerfLabel(p.label)}</div>
                        <div className="text-[9px] text-slate-500 pr-6">{p.time || "TBD"}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
      <div className="sticky left-0 flex items-center gap-4 px-3 py-3 text-[11px] text-slate-500 flex-wrap" style={{ maxWidth: "100vw" }}>
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded bg-[#DCEFC4] border border-[#8CC152]" />Your pick</span>
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-[#C8000F] text-white text-[9px] font-extrabold flex items-center justify-center">1</span>1st choice</span>
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-[#0C1017] text-white text-[9px] font-extrabold flex items-center justify-center">2</span>2nd choice</span>
        <span className="flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-[#16A34A] text-white flex items-center justify-center"><Check className="w-2.5 h-2.5" strokeWidth={3} /></span>Entered</span>
        <span>Perf = Performance · Slack = Slack performance · click the circle to set 1st / 2nd, the check to mark entered</span>
      </div>
    </div>
  );
}

function ClashPanel({ dayChecks, inset }) {
  if (!dayChecks.length) return null;
  return (
    <div className={`rounded-xl border border-[#E4E7EB] bg-white p-4 space-y-3 ${inset ? "m-4" : ""}`} data-testid="nv-daycheck">
      <div className="flex items-center gap-2 font-display font-bold text-sm text-slate-800"><Clock className="w-4 h-4 text-[#1D4ED8]" /> Same-day travel check (your 1st choices)</div>
      {dayChecks.map((dc) => {
        const V = { ok: { c: "#3B6B1E", bg: "#EAF5E8", bd: "#CDE8C4", t: "Makeable" }, tight: { c: "#8A5A00", bg: "#FFF6E9", bd: "#F0D9AE", t: "Very tight" }, unknown: { c: "#475569", bg: "#F1F3F5", bd: "#E4E7EB", t: "Location unknown" }, clash: { c: "#9E0008", bg: "#FDECEE", bd: "#F3C6CB", t: "Won't make it" } }[dc.worst];
        return (
          <div key={dc.date} className="rounded-lg border p-3" style={{ background: V.bg, borderColor: V.bd }} data-testid={`nv-daycheck-${dc.date}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{new Date(dc.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</span>
              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full" style={{ background: V.c, color: "#fff" }}>{V.t}</span>
            </div>
            <div className="space-y-1.5">
              {dc.legs.map((leg, i) => {
                const TXT = { time: { ok: "makeable", tight: "very tight", clash: "can't make it — trade one" }, dist: { ok: "far enough apart it should work", tight: "maybe — depends on start times", clash: "too far to make both" }, "no-loc": { unknown: "location not on file" } };
                const C = { ok: "#3B6B1E", tight: "#8A5A00", clash: "#9E0008", unknown: "#475569" }[leg.verdict];
                const t = (TXT[leg.basis] && TXT[leg.basis][leg.verdict]) || "check manually";
                return (
                  <div key={i} className="text-[12px] text-slate-700">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="w-4 h-4 rounded-full bg-[#C8000F] text-white text-[9px] font-extrabold flex items-center justify-center">1</span>
                      <span className="font-semibold">{leg.a.name}</span><span className="text-slate-400">{leg.a.time || "time TBD"}</span>
                      <span className="text-slate-300">→</span>
                      <span className="w-4 h-4 rounded-full bg-[#C8000F] text-white text-[9px] font-extrabold flex items-center justify-center">1</span>
                      <span className="font-semibold">{leg.b.name}</span><span className="text-slate-400">{leg.b.time || "time TBD"}</span>
                    </div>
                    <div className="pl-6 text-[11px] text-slate-500">
                      {leg.km != null ? `≈ ${leg.km.toLocaleString()} km · ~${leg.driveH.toFixed(1)} h ${leg.routed ? "by road" : "drive (est.)"}` : "distance unknown"}
                      {leg.gapH != null ? ` · ${leg.gapH.toFixed(1)} h between starts` : (leg.basis === "dist" ? " · start times not posted" : "")}
                      <span className="font-bold ml-1" style={{ color: C }}>→ {t}</span>
                      {leg.pending && <span className="text-slate-400 ml-1">· checking real roads…</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
